import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

// Module-level guard: ensures SSO fires exactly once per page load
// across React StrictMode double-mount and any other re-renders.
let _ssoAttempted = false;

export default function Login() {
    const { login, loginWithSSO } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const ssoFired = useRef(false);

    // Auto-detect SSO token from Luxsun Hub
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ssoToken = params.get("luxsun_token");
        if (!ssoToken) return;
        // Token-bound guard: avoid duplicate calls in dev StrictMode and on re-mount
        const inflightKey = "gb_sso_inflight";
        if (sessionStorage.getItem(inflightKey) === ssoToken) return;
        sessionStorage.setItem(inflightKey, ssoToken);
        ssoFired.current = true;
        setSsoLoading(true);
        loginWithSSO(ssoToken)
            .then(() => {
                // Strip SSO params and do a full reload so auth context picks up fresh localStorage.
                // Using location.replace() ensures the SSO token never lands in history/Referer.
                toast.success("Velkommen — logget ind via Luxsun Hub");
                window.location.replace("/dashboard");
            })
            .catch((err) => {
                toast.error(err.response?.data?.detail || "SSO-login fejlede");
                setSsoLoading(false);
                // Clean URL even on failure so user can try logging in normally
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Velkommen");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login fejlede");
        } finally {
            setLoading(false);
        }
    };

    if (ssoLoading) {
        return (
            <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
                <div className="text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#78736E] mx-auto mb-3" />
                    <p className="text-sm text-[#78736E]">Logger ind via Luxsun Hub…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
            <div className="w-full max-w-sm">
                <div className="flex justify-center mb-10">
                    <Logo to="/" size="lg" />
                </div>

                <div className="bg-white border border-[#E5E1DA] rounded-2xl p-8 shadow-[0_8px_32px_rgba(44,42,41,0.04)]">
                    <h1 className="font-heading text-2xl font-medium text-[#2C2A29] mb-1">Log ind</h1>
                    <p className="text-sm text-[#78736E] mb-6">Medarbejder-adgang til reklamationsdashboardet</p>

                    <form onSubmit={submit} className="space-y-5">
                        <div className="space-y-1.5">
                            <Label className="text-sm">Email</Label>
                            <Input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                placeholder="navn@gardinbussen.org"
                                autoComplete="username"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">Adgangskode</Label>
                            <Input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                autoComplete="current-password"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="w-full bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Log ind
                        </Button>
                    </form>
                </div>

                <div className="text-center mt-6 text-xs text-[#78736E]">
                    <Link to="/" className="hover:underline">← Tilbage til forside</Link>
                </div>
            </div>
        </div>
    );
}
